import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { noticesService } from '../services/noticesService';
import type { Notice } from '../types/notices';

/**
 * Renders a full-width purple banner when there is an unread "reload" notice.
 * Reads from the shared ['notices'] query cache (same as NoticesButton — no extra fetch).
 *
 * "Reload Now" marks the notice as read then reloads the page.
 * The dismiss (X) button marks as read without reloading.
 */
export const ReloadBanner: React.FC = () => {
  const queryClient = useQueryClient();

  const { data: notices = [] } = useQuery({
    queryKey: ['notices'],
    queryFn: () => noticesService.getNotices(),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const reloadNotice = notices.find(
    (n: Notice) => n.notice_type === 'reload' && !n.is_read
  );

  const markReadMutation = useMutation({
    mutationFn: (noticeId: number) => noticesService.markAsRead(noticeId),
    onSuccess: (_, noticeId) => {
      queryClient.setQueryData<Notice[]>(['notices'], (old) =>
        old?.map((n) => (n.id === noticeId ? { ...n, is_read: true } : n))
      );
    },
  });

  if (!reloadNotice) {
    return null;
  }

  const handleReload = (): void => {
    markReadMutation.mutate(reloadNotice.id, {
      onSettled: () => {
        window.location.reload();
      },
    });
  };

  const handleDismiss = (): void => {
    markReadMutation.mutate(reloadNotice.id);
  };

  return (
    <div className="bg-purple-500 bg-opacity-10 border-b border-purple-500 px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4 text-purple-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
        </svg>
        <span className="text-sm text-gray-300">
          {reloadNotice.title}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleReload}
          className="px-3 py-1 text-sm font-medium bg-purple-500 hover:bg-purple-600 text-white rounded transition-colors"
        >
          Reload Now
        </button>
        <button
          onClick={handleDismiss}
          className="text-gray-500 hover:text-gray-400 transition-colors"
          aria-label="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};
